// src/pages/gameweeks/AssignMatchesModal.tsx
import { useState, useEffect, useMemo } from "react";
import { Loader2, Link2, AlertTriangle, Info, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useMatchesAdmin } from "@/hooks/api/useMatches";
import type { Gameweek, Match } from "@/types/api.types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gameweek: Gameweek | null;
  onAssign: (matchIds: number[]) => void;
  isAssigning: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: "bg-blue-100 text-blue-700",
  LIVE: "bg-red-100 text-red-700",
  SCHEDULED: "bg-gray-100 text-gray-600",
};

/** Extract the group label from a stage string like "Group A - Matchday 1" → "Group A" */
function extractGroup(stage: string): string {
  const match = stage.match(/Group\s+[A-Z]/i);
  return match ? match[0] : stage || "Other";
}

/** Group a list of matches by their group label. Returns sorted [groupLabel, matches[]] pairs. */
function groupByGroupLabel(matchList: Match[]): [string, Match[]][] {
  const map: Record<string, Match[]> = {};
  for (const m of matchList) {
    const key = extractGroup(m.stage ?? "");
    if (!map[key]) map[key] = [];
    map[key].push(m);
  }
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
}

export default function AssignMatchesModal({
  open,
  onOpenChange,
  gameweek,
  onAssign,
  isAssigning,
}: Props) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showAllPhase, setShowAllPhase] = useState(false);
  const [linkedExpanded, setLinkedExpanded] = useState(true);

  const isGroupStage = gameweek?.phase === "GROUP_STAGE";
  const expectedMatchday = isGroupStage
    ? `Matchday ${gameweek?.roundNumber}`
    : null;
  const isFinalGroupMatchday = isGroupStage && gameweek?.roundNumber === 3;

  const { data: matchesData, isLoading } = useMatchesAdmin(
    open ? { phase: gameweek?.phase as Match["phase"] } : undefined
  );

  const allPhaseMatches: Match[] = matchesData?.matches ?? [];

  // Smart filter: for GROUP_STAGE, narrow to the expected matchday
  const smartFilteredMatches = useMemo(() => {
    if (!isGroupStage || showAllPhase || !expectedMatchday) {
      return allPhaseMatches;
    }
    const filtered = allPhaseMatches.filter((m) =>
      m.stage?.toLowerCase().includes(expectedMatchday.toLowerCase())
    );
    // Graceful fallback: if filter yields nothing, show all phase matches
    return filtered.length > 0 ? filtered : allPhaseMatches;
  }, [allPhaseMatches, isGroupStage, showAllPhase, expectedMatchday]);

  const smartFilterActive =
    isGroupStage &&
    !showAllPhase &&
    !!expectedMatchday &&
    smartFilteredMatches.length < allPhaseMatches.length;

  // Split into: linked to this GW | available (unassigned) | locked to other GW
  const linkedHere = smartFilteredMatches.filter(
    (m) => m.gameweekId === gameweek?.id
  );
  const available = smartFilteredMatches.filter(
    (m) => m.gameweekId === null || m.gameweekId === undefined
  );
  const lockedElsewhere = allPhaseMatches.filter(
    (m) => m.gameweekId !== null && m.gameweekId !== undefined && m.gameweekId !== gameweek?.id
  );

  // Pre-select matches already linked to this gameweek
  useEffect(() => {
    if (!open || !gameweek || isLoading) return;
    const preSelected = allPhaseMatches
      .filter((m) => m.gameweekId === gameweek.id)
      .map((m) => m.id);
    setSelected(new Set(preSelected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gameweek?.id, isLoading]);

  // Reset filter toggle when modal closes
  useEffect(() => {
    if (!open) {
      setShowAllPhase(false);
      setLinkedExpanded(true);
    }
  }, [open]);

  const toggleMatch = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllAvailable = () => {
    const availableIds = available.map((m) => m.id);
    const allSelected = availableIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        availableIds.forEach((id) => next.delete(id));
      } else {
        availableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const availableSelectedCount = available.filter((m) => selected.has(m.id)).length;
  const allAvailableSelected =
    available.length > 0 && availableSelectedCount === available.length;

  const handleSubmit = () => {
    if (selected.size === 0) return;
    onAssign(Array.from(selected));
  };

  const groupedAvailable = useMemo(
    () => (isGroupStage ? groupByGroupLabel(available) : null),
    [available, isGroupStage]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-600" />
            Assign Matches — {gameweek?.name}
          </DialogTitle>
          <DialogDescription>
            Select which matches belong to this gameweek. Only assigned matches
            are used when calculating points.
          </DialogDescription>
        </DialogHeader>

        {/* Info banners */}
        <div className="space-y-2">
          {/* Matchweek 3 final round notice */}
          {isFinalGroupMatchday && (
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <Info className="h-4 w-4 shrink-0 mt-0.5 text-blue-500" />
              <span>
                <strong>Final Group Stage Matchday.</strong> All matches within
                each group should kick off simultaneously. After completing
                these matches, eliminate bottom-2 nations per group and advance
                to Round of 32.
              </span>
            </div>
          )}

          {/* Smart filter active notice */}
          {smartFilterActive && (
            <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-sm text-indigo-800">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 shrink-0 text-indigo-500" />
                <span>
                  Showing only <strong>{expectedMatchday}</strong> matches for
                  this gameweek.
                </span>
              </div>
              <button
                className="text-xs underline text-indigo-600 hover:text-indigo-800 whitespace-nowrap ml-3"
                onClick={() => setShowAllPhase(true)}
              >
                Show all Group Stage
              </button>
            </div>
          )}
          {showAllPhase && isGroupStage && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
              <span>Showing all Group Stage matches (smart filter off).</span>
              <button
                className="text-xs underline text-amber-700 hover:text-amber-900 ml-3"
                onClick={() => setShowAllPhase(false)}
              >
                Re-apply {expectedMatchday} filter
              </button>
            </div>
          )}

          {/* Locked elsewhere warning */}
          {lockedElsewhere.length > 0 && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
              <span>
                {lockedElsewhere.length} match(es) are already assigned to
                another gameweek and are not shown below.
              </span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="overflow-y-auto max-h-[52vh] space-y-4 pr-1">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* ── Section 1: Already linked to this gameweek ── */}
              {linkedHere.length > 0 && (
                <div className="rounded-lg border border-green-200 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 bg-green-50 hover:bg-green-100 transition-colors"
                    onClick={() => setLinkedExpanded((v) => !v)}
                  >
                    <span className="text-sm font-semibold text-green-800 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                      Assigned to this gameweek ({linkedHere.length})
                    </span>
                    {linkedExpanded ? (
                      <ChevronUp className="h-4 w-4 text-green-700" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-green-700" />
                    )}
                  </button>

                  {linkedExpanded && (
                    <div className="divide-y divide-green-100">
                      {linkedHere.map((match) => (
                        <label
                          key={match.id}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                            selected.has(match.id)
                              ? "bg-green-50"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(match.id)}
                            onChange={() => toggleMatch(match.id)}
                            className="h-4 w-4 rounded border-gray-300 text-green-600 cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-gray-900">
                                {match.homeNation?.name ?? "?"} vs{" "}
                                {match.awayNation?.name ?? "?"}
                              </span>
                              {match.status === "COMPLETED" && (
                                <span className="text-xs text-gray-500">
                                  {match.homeScore} – {match.awayScore}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {match.stage || "—"}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Badge
                              className={`text-xs ${STATUS_COLORS[match.status] ?? "bg-gray-100 text-gray-600"}`}
                            >
                              {match.status}
                            </Badge>
                            <Badge className="text-xs bg-green-100 text-green-700">
                              Linked
                            </Badge>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Section 2: Available (unassigned) matches ── */}
              {available.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-24 text-gray-400 gap-2">
                  <span className="text-3xl">🏟️</span>
                  <p className="text-sm text-center">
                    No unassigned matches found
                    {expectedMatchday && !showAllPhase
                      ? ` for ${expectedMatchday}`
                      : ` for phase ${gameweek?.phase}`}
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  {/* Select-all header */}
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={allAvailableSelected}
                        onChange={toggleAllAvailable}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                      />
                      <span className="text-sm font-semibold text-gray-700">
                        Available to assign ({available.length})
                      </span>
                    </label>
                    <span className="text-xs text-gray-400">
                      {availableSelectedCount} selected
                    </span>
                  </div>

                  {/* Grouped by group (GROUP_STAGE) or flat list */}
                  {isGroupStage && groupedAvailable ? (
                    <div className="divide-y divide-gray-100">
                      {groupedAvailable.map(([groupLabel, groupMatches]) => (
                        <div key={groupLabel}>
                          <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                            {groupLabel}
                          </div>
                          {groupMatches.map((match) => (
                            <MatchRow
                              key={match.id}
                              match={match}
                              checked={selected.has(match.id)}
                              onToggle={() => toggleMatch(match.id)}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {available.map((match) => (
                        <MatchRow
                          key={match.id}
                          match={match}
                          checked={selected.has(match.id)}
                          onToggle={() => toggleMatch(match.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t pt-3 gap-2 mt-1">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isAssigning}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selected.size === 0 || isAssigning}
            className="bg-blue-600 hover:bg-blue-700 text-white min-w-[140px]"
          >
            {isAssigning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning…
              </>
            ) : (
              `Assign ${selected.size} Match${selected.size !== 1 ? "es" : ""}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MatchRow({
  match,
  checked,
  onToggle,
}: {
  match: Match;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
        checked
          ? "border-blue-400 bg-blue-50"
          : "bg-white hover:bg-gray-50"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-gray-900">
            {match.homeNation?.name ?? "?"} vs {match.awayNation?.name ?? "?"}
          </span>
          {match.status === "COMPLETED" && (
            <span className="text-xs text-gray-500">
              {match.homeScore} – {match.awayScore}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-gray-400">{match.stage || "—"}</span>
          {match._count?.events != null && (
            <span className="text-xs text-gray-400">
              · {match._count.events} events
            </span>
          )}
        </div>
      </div>
      <Badge
        className={`text-xs shrink-0 ${STATUS_COLORS[match.status] ?? "bg-gray-100 text-gray-600"}`}
      >
        {match.status}
      </Badge>
    </label>
  );
}
